import React, { useEffect, useState } from 'react';
import { bedrockUsageService, CallLogRow } from '../../services/bedrockUsageService';
import '../../styles/BedrockUsage.css';

function formatDateTime(isoString: string): string {
  return new Date(isoString).toLocaleString();
}

function lastModelSegment(modelId: string): string {
  if (!modelId) return '—';
  const idx = modelId.lastIndexOf('/');
  return idx >= 0 ? modelId.slice(idx + 1) : modelId;
}

const PAGE_SIZE = 50;

interface Props {
  userId: number;
  username: string;
  orgId: number;
  startDate: string;
  endDate: string;
}

const CallLogPanel: React.FC<Props> = ({ userId, username, orgId, startDate, endDate }) => {
  const [rows, setRows] = useState<CallLogRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPage(1);
  }, [userId, orgId, startDate, endDate]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    bedrockUsageService
      .getUserCallLog(userId, orgId, page, PAGE_SIZE, startDate || undefined, endDate || undefined)
      .then((result) => {
        if (!cancelled) {
          setRows(result.rows);
          setTotal(result.total);
          setLoading(false);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [userId, orgId, page, startDate, endDate]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const startRecord = (page - 1) * PAGE_SIZE + 1;
  const endRecord = Math.min(page * PAGE_SIZE, total);

  if (loading) {
    return (
      <div className="usage-table-container">
        <div className="usage-loading">Loading call log for {username}…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="usage-table-container">
        <div className="usage-empty" style={{ color: 'var(--error-color, #f44336)' }}>
          Error: {error}
        </div>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="usage-table-container">
        <div className="usage-empty">No call log entries found for {username} in the selected period.</div>
      </div>
    );
  }

  return (
    <div className="usage-table-container">
      <table className="usage-table">
        <thead>
          <tr>
            <th>Timestamp</th>
            <th>Model</th>
            <th>Request Type</th>
            <th>Input Tokens</th>
            <th>Output Tokens</th>
            <th>Total Tokens</th>
            <th>Tool Servers</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>{formatDateTime(row.created_at)}</td>
              <td title={row.model_id}>{lastModelSegment(row.model_id)}</td>
              <td>{row.request_type}</td>
              <td>{row.input_tokens != null ? row.input_tokens.toLocaleString() : '—'}</td>
              <td>{row.output_tokens != null ? row.output_tokens.toLocaleString() : '—'}</td>
              <td>{row.actual_tokens.toLocaleString()}</td>
              <td>
                {row.tool_server_ids.length === 0
                  ? 'Direct'
                  : row.tool_server_ids.length === 1
                  ? `1 server`
                  : `${row.tool_server_ids.length} servers`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="usage-pagination">
        <span>
          Showing {startRecord}–{endRecord} of {total.toLocaleString()} records
        </span>
        <div className="usage-pagination-buttons">
          <button
            className="usage-pagination-btn"
            onClick={() => setPage((p) => p - 1)}
            disabled={page <= 1}
          >
            Previous
          </button>
          <button
            className="usage-pagination-btn"
            onClick={() => setPage((p) => p + 1)}
            disabled={page >= totalPages}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default CallLogPanel;
